import { useState, useEffect, useMemo, useRef } from "react";
import {
  Alert,
  Button,
  Modal,
  Segmented,
  Select,
  Tag,
  TimePicker,
  message,
} from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  LoginOutlined,
  LogoutOutlined,
  PlayCircleFilled,
} from "@ant-design/icons";
import ClockFaceTimePicker from "./ClockFaceTimePicker";
import {
  getDisabledBeforeCustomHours,
  getDisabledBeforeCustomMinutes,
  getDisabledAfterCustomHours,
  getDisabledAfterMinutes,
} from "../../../dashboard/utils/disabledTimeFilters";
import { createNewShiftByAdmin } from "../../../Pagat/approvals/utils";
import { useAuth } from "../../../../authentication/authHelpers";
import SuccessComponent from "../../../commonComponents/SuccessComponent/SuccessComponent";
import { RichTextEditor } from "../../../commonComponents";
import { useDispatch, useSelector } from "react-redux";
import CrashShift from "../../../Pagat/approvals/crashShiftModal/CrashShift";
import { calculateDayType } from "../../utils/calculateDayType";
import useReactIpLocation from "react-ip-details";
import { publish } from "../../../../utils/socket/dashboardSocket";
import { addClocking } from "../../../../store/actions";
import axios from "axios";
import dayjsTZ from "../../../../utils/dayjs";
import { resolveTeamTimezone } from "../../../../utils/timezone/teamTimezone";
import { eventTracker } from "../../../../utils/eventTracker";
import {
  fetchScheduledShift,
  resolvePlannedShift,
} from "../../../../utils/scheduling/findScheduledShift";
import {
  canBypassGrace,
  evaluateGracePeriod,
  getGracePeriodMinutes,
  graceInfoMessage,
  lateNoteWarningText,
  overnightLateNoteWarningText,
  isLateClockIn,
  OVERNIGHT_LATE_BEFORE_HOUR,
  MIN_NOTE_CHARS,
  noteCharCount as countNoteChars,
  noteMeetsMinLength,
} from "./gracePeriod";
import {
  evaluatePlannedClockInWindow,
  getEditTimeCeiling,
  isAboveEditCeiling,
  plannedWindowHint,
} from "./plannedWindow";
import "./shiftModal.scss";

const formatHm = (totalMinutes) => {
  const m = Math.max(0, Math.round(totalMinutes || 0));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

const PICKER_MODE_KEY = "flexhr.pickerMode";
const readPickerMode = () => {
  try {
    const v = window.localStorage.getItem(PICKER_MODE_KEY);
    return v === "clock" ? "clock" : "classic";
  } catch (_) {
    return "classic";
  }
};

// * @EneaXharau - Websocket handler added
const CLOCKIN_TYPES_FIELD_ID = "e685cd30-b119-11ec-829e-112b583587c7";

const { Option } = Select;

function NewUserShift({
  selfType,
  newShiftVisib,
  setNewShiftVisib,
  onRefresh,
  setPrevShift,
  liveTime,
  shiftConfiguration,
}) {
  const auth = useAuth();
  const dispatch = useDispatch();

  const { programFields } = useSelector((state) => state.programFields);
  const { employeeRequests } = useSelector((state) => state.employeeRequests);
  const { isDarkMode } = useSelector((state) => state.usersList);
  const { employees } = useSelector((state) => state.employeesList);

  const [newShift, setNewShift] = useState({
    clockInDate: dayjsTZ(),
    clockOutDate: null,
    note: "",
    employeeId: null,
  });
  const [dateDayJs, setDateDayJs] = useState(dayjsTZ());
  const [saving, setSaving] = useState(false);
  const [crashError, setCrashError] = useState(false);
  const [crashData, setCrashData] = useState([]);
  const [successVisibility, setSuccessVisibility] = useState(false);
  const [dayType, setDayType] = useState(null);
  const [gracePeriod, setGracePeriod] = useState(0);
  const [graceWarnings, setGraceWarning] = useState({
    enable: false,
    text: "",
  });
  const [ip, setIp] = useState("");
  const [manualShift, setManualShift] = useState(false);
  // Scheduled-shift override: when the rotation system has published an
  // assignment for this employee on this day, we use it as the canonical
  // planned shift (preferred over the static employee-shift link).
  // `undefined` = not yet fetched; `null` = fetched, no schedule;
  // object   = fetched, resolved shift.
  const [scheduledShift, setScheduledShift] = useState(undefined);
  const [pickerMode, setPickerMode] = useState(readPickerMode);
  useEffect(() => {
    try {
      window.localStorage.setItem(PICKER_MODE_KEY, pickerMode);
    } catch (_) {}
  }, [pickerMode]);
  //   const { ipResponse } = useReactIpLocation();

  const { ipResponse } = useReactIpLocation({ numberToConvert: 100 });
  const clockinRef = useRef();

  const graceBypass = canBypassGrace(auth?.userRole);
  const teamTimezone = resolveTeamTimezone(
    auth?.employee?.employeeTeam,
    programFields
  );

  //region ON SAVE
  const onSave = async () => {
    if (!newShift.clockInDate || !newShift.employeeId || dayType === null) {
      message.error({
        key: "newShift",
        content:
          "Gabim në plotësimin e të dhënave! Ju lutem plotësoni të gjitha fushat!",
      });
      return;
    }

    let date1 = dayjsTZ();
    let clockIn = dayjsTZ(newShift.clockInDate);
    let date2 = dayjsTZ();
    let clockOut = dayjsTZ(newShift.clockOutDate);
    date1 = date1
      .set("hour", clockIn.get("hour"))
      .set("minute", clockIn.get("minute"))
      .set("second", "00");
    date2 = date2
      .set("hour", clockOut.get("hour"))
      .set("minute", clockOut.get("minute"))
      .set("second", "00");

    // Soft schedule: warn + mandatory note — never block for off-schedule/late.
    const scheduleCheck = evaluatePlannedClockInWindow(plannedShift, date1, {
      timezone: teamTimezone,
    });
    const legacyLate =
      !plannedShift?.start && isLateClockIn(date1, null);
    const noteRequiredNow =
      scheduleCheck.noteRequired || legacyLate || graceWarnings.enable;
    if (noteRequiredNow && !noteMeetsMinLength(newShift?.note)) {
      setGraceWarning({
        text:
          scheduleCheck.reason ||
          (legacyLate
            ? overnightLateNoteWarningText()
            : lateNoteWarningText()),
        enable: true,
      });
      message.error({
        key: "newShift",
        content: `Shënimi me arsye është i detyrueshëm (min. ${MIN_NOTE_CHARS} karaktere).`,
      });
      return;
    }

    setSaving(true);
    eventTracker("Clock In", "click", "Started shift");

    const toSend = {
      clockInDate: dayjsTZ(date1).valueOf(),
      clockOutDate:
        newShift.clockOutDate !== null ? dayjsTZ(date2).valueOf() : null,
      employeeId: newShift.employeeId,
      timezone: teamTimezone,
      clockingCategory: "user",
      dayType: dayType,
      clockInNote: newShift?.note || null,
      clockOutNote: "",
      editedClockIn:
        dayjsTZ().format("HH:mm") !== dayjsTZ(date1).format("HH:mm")
          ? true
          : false,
      editedClockOut: false,
      editedByAdmin: false,
      forgotClockOut: false,
      approved: false,
      clockingLogs: [
        {
          createdAt: dayjsTZ().valueOf(),
          note: "Krijuar nga vetë punonjësi.",
          newShiftByAdmin: false,
          newShiftByUser: true,
          username: auth?.user?.given_name + " " + auth?.user?.family_name,
          userSub: auth?.user?.userSub,
          ipResponse: {
            ...ipResponse,
            IPv4: ip,
          },
        },
      ],
    };

    await createNewShiftByAdmin(
      toSend,
      newShift.employeeId,
      employeeRequests,
      employees
    )
      .then(async (res) => {
        dispatch(addClocking(res));
        setNewShiftVisib(false);
        setSuccessVisibility(true);

        // Admin OS toast: AuthenticatedRoute maps user_clockIn_received.
        publish({
          request: "user_clockIn",
          body: {
            ...res,
            username:
              `${auth?.user?.given_name || ""} ${
                auth?.user?.family_name || ""
              }`.trim() || res?.username,
          },
          id: newShift.employeeId,
        });
      })
      .catch((errArr) => {
        if (errArr?.[0] === "approved_leave") {
          return;
        }
        setCrashError(true);
        setCrashData(errArr);
      });
    setSaving(false);
    setPrevShift(true);
  };
  const handleReset = () => {
    setNewShift({
      clockInDate: dayjsTZ(),
      clockOutDate: null,
      note: "",
      employeeId: null,
    });
    setDateDayJs(dayjsTZ());
    onRefresh();
  };

  //region >checkGracePeriod()
  const checkGracePeriod = (date) => {
    const result = evaluateGracePeriod(date, gracePeriod, {
      bypass: graceBypass,
    });
    setGraceWarning({
      text: result.noteRequired ? result.text : "",
      enable: result.noteRequired,
    });
    return result.allowed;
  };

  //region EDIT CLOCKIN
  const onEditClockIn = (e) => {
    let date = dayjsTZ();
    let time = dayjsTZ(e)
      .set("hour", e.get("hour"))
      .set("minute", e.get("minute"))
      .set("second", 0);

    setGraceWarning({
      text: "",
      enable: false,
    });

    const suggested = getAutoSuggest(dayjsTZ());
    const ceiling = getEditTimeCeiling(dayjsTZ(), suggested, {
      bypass: graceBypass,
      timezone: teamTimezone,
    });
    if (isAboveEditCeiling(time, ceiling, teamTimezone)) {
      message.error({
        key: "newShift",
        content: "Nuk mund të zgjedhësh një kohë në të ardhmen!",
      });
      setNewShift((prev) => ({ ...prev, clockInDate: suggested }));
      return;
    }

    if (gracePeriod === 0 && !graceBypass) {
      message.error({
        key: "newShift",
        content: "Ju nuk mund të ndryshoni orën e fillimit!",
      });
      if (isLateClockIn(dayjsTZ(), plannedShift)) {
        const overnight =
          !plannedShift?.start &&
          dayjsTZ().hour() < OVERNIGHT_LATE_BEFORE_HOUR;
        setGraceWarning({
          text: overnight
            ? overnightLateNoteWarningText()
            : lateNoteWarningText(),
          enable: true,
        });
      }
      return;
    } else {
      date = date
        .set("hour", e.get("hour"))
        .set("minute", e.get("minute"))
        .set("second", e.get("second"));

      const graceTime = checkGracePeriod(date);
      const scheduleCheck = evaluatePlannedClockInWindow(plannedShift, date, {
        timezone: teamTimezone,
      });
      if (scheduleCheck.noteRequired) {
        setGraceWarning({
          text: scheduleCheck.reason,
          enable: true,
        });
      }

      if (e !== null && newShift.clockOutDate !== null && graceTime) {
        if (
          dayjsTZ(date).valueOf() < dayjsTZ(newShift.clockOutDate).valueOf()
        ) {
          setNewShift((prev) => ({
            ...prev,
            clockInDate: date,
            editedClockIn: true,
          }));
          setManualShift(true);
        } else {
          message.error({
            key: "newShift",
            content: "Fillimi i turnit nuk mund të jetë më vonë se mbarimi.",
          });
        }
      } else if (!graceTime) {
        message.error({
          key: "newShift",
          content:
            "Ju nuk mund të ndryshoni orën e fillimit më shumë se vlera e lejuar!",
        });
        setGraceWarning({
          text: "Nevojitet një shënim!",
          enable: true,
        });
        setNewShift((prev) => ({ ...prev, clockInDate: dayjsTZ() }));
      } else {
        setNewShift((prev) => ({ ...prev, clockInDate: date }));
        setManualShift(true);
      }
    }
  };

  //region EDIT CLOCKOUT
  const onEditClockOut = (e) => {
    let date2 = dayjsTZ();

    date2 = date2
      .set("hour", e.get("hour"))
      .set("minute", e.get("minute"))
      .set("second", e.get("second"));

    if (e !== null && newShift.clockInDate !== null) {
      // changed clockIn
      if (dayjsTZ(date2).valueOf() > dayjsTZ(newShift.clockInDate).valueOf()) {
        setNewShift((prev) => ({ ...prev, clockOutDate: date2 }));
      } else {
        message.error({
          key: "newShift",
          content: "Mbarimi i turnit nuk mund të jetë më shpejt se fillimi.",
        });
      }
    } else {
      setNewShift((prev) => ({ ...prev, clockInDate: date2 }));
    }
  };

  const onCalendarPick = (e) => {
    if (e !== null && e !== undefined) {
      setDateDayJs(e);
    }
  };

  //region AUTO SUGGEST
  // Derived window-snapper: if the live time is within
  // `[plannedShift.start − 15min, plannedShift.start + 1min30s]` we snap
  // to the planned start; otherwise we return "now". Without a planned
  // shift we keep the legacy two-window fallback so existing employees
  // (no rotation policy yet) see no behavior change.
  const snapToPlannedStart = (time, plannedStart) => {
    if (!plannedStart) return null;
    const t = dayjsTZ(time).valueOf();
    const startMs = dayjsTZ(plannedStart).valueOf();
    const lower = startMs - 15 * 60 * 1000;
    const upper = startMs + 90 * 1000;
    if (t > lower && t < upper) {
      return dayjsTZ(plannedStart).set("second", 0);
    }
    return null;
  };

  const legacyAutoSuggest = (time) => {
    // Preserved fallback: snap to 13:30 OR 10:00 depending on which
    // window the live time lands in. Used only when no plannedShift
    // resolved (i.e. employee has no rotation assignment AND no static
    // shift link).
    const t = dayjsTZ(time).valueOf();
    const afternoonStart = dayjsTZ().set("hour", 13).set("minute", 30).set("second", 0);
    const morningStart = dayjsTZ().set("hour", 10).set("minute", 0).set("second", 0);

    const inMorningWindow =
      t > dayjsTZ().set("hour", 9).set("minute", 40).set("second", 0).valueOf() &&
      t < dayjsTZ().set("hour", 10).set("minute", 1).set("second", 30).valueOf();
    if (inMorningWindow) return morningStart;

    const inAfternoonWindow =
      t > dayjsTZ().set("hour", 13).set("minute", 15).set("second", 0).valueOf() &&
      t < dayjsTZ().set("hour", 13).set("minute", 31).set("second", 30).valueOf();
    if (inAfternoonWindow) return afternoonStart;

    return dayjsTZ();
  };

  const getAutoSuggest = (time) => {
    const snapped = snapToPlannedStart(time, plannedShift?.start);
    if (snapped) return snapped;
    if (plannedShift?.start) return dayjsTZ();
    return legacyAutoSuggest(time);
  };
  const leaves = useMemo(() => {
    if (!!Object.keys(auth?.user).length && !!employeeRequests) {
      return (employeeRequests || []).filter(
        (el) => el?.userSub === auth?.user?.userSub
      );
    } else return [];
  }, [auth?.user, employeeRequests]);

  const getIp = async () => {
    const res = await axios.get("https://api.ipify.org/?format=json");
    setIp(res?.data?.ip);
  };

  useEffect(() => {
    getIp();
  }, []);

  useEffect(() => {
    if (selfType === true && !!Object.keys(auth).length) {
      setNewShift((prev) => ({
        ...prev,
        employeeId: auth?.employeeId || null,
      }));
    }
  }, [selfType, auth]);

  // Fetch the rotation-system assignment for today when the modal opens.
  // Safe to call unconditionally — backend returns `{ shift: null }` when
  // no published schedule covers this employee yet, and the resolver
  // helper degrades to `null` on any error so the legacy fallback kicks in.
  useEffect(() => {
    if (!newShiftVisib) return;
    const employeeId = auth?.employeeId || newShift.employeeId;
    if (!employeeId) return;
    let cancelled = false;
    (async () => {
      const res = await fetchScheduledShift(
        employeeId,
        Date.now(),
        teamTimezone
      );
      if (!cancelled) setScheduledShift(res || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [newShiftVisib, auth?.employeeId, newShift.employeeId]);

  // Planned hours: rotation for the day wins; else this employee's static
  // link to one of the two shifts (10:00–18:30 / 13:30–21:30).
  const plannedShift = useMemo(() => {
    const applied = Array.isArray(shiftConfiguration?.appliedShifts)
      ? shiftConfiguration.appliedShifts.filter(Boolean)
      : [];
    const catalogue = Array.isArray(shiftConfiguration?.applicableShifts)
      ? shiftConfiguration.applicableShifts.filter(Boolean)
      : [];
    return resolvePlannedShift(scheduledShift, applied, {
      at: dayjsTZ(),
      timezone: teamTimezone,
      catalogueShifts: catalogue,
    });
  }, [scheduledShift, shiftConfiguration, teamTimezone]);

  useEffect(() => {
    if (leaves.length >= 0 && !!Object.keys(programFields || {}).length) {
      let test = calculateDayType(
        programFields,
        leaves,
        newShift.clockInDate,
        auth?.employee?.employeeTeam
      );
      setGracePeriod(getGracePeriodMinutes(programFields));

      // Soft verification vs this employee's planned hours: warn + require
      // a reason note (late / early / off-schedule). Never hard-block.
      const clockAt = newShift?.clockInDate
        ? dayjsTZ(newShift.clockInDate)
        : dayjsTZ();
      const scheduleCheck = evaluatePlannedClockInWindow(
        plannedShift,
        clockAt,
        { timezone: teamTimezone }
      );
      if (scheduleCheck.noteRequired) {
        setGraceWarning({
          text: scheduleCheck.reason,
          enable: true,
        });
      } else if (!plannedShift?.start && isLateClockIn(clockAt, null)) {
        const overnight = clockAt.hour() < OVERNIGHT_LATE_BEFORE_HOUR;
        setGraceWarning({
          text: overnight
            ? overnightLateNoteWarningText()
            : lateNoteWarningText(),
          enable: true,
        });
      } else {
        setGraceWarning({ text: "", enable: false });
      }
      // else if (
      // 	dayjsTZ().diff(dayjsTZ(tempTime), "minutes", true) < 1 ||
      // 	dayjsTZ().diff(dayjsTZ(tempTime2), "minutes", true) < 1
      // ) {
      // 	setGraceWarning({
      // 		text: "",
      // 		enable: false,
      // 	});
      // }

      if (!!test) {
        setDayType(test);
      } else {
        setDayType();
      }
    } else {
      setDayType("Regular");
    }
  }, [
    leaves,
    newShift,
    programFields,
    auth?.employee?.employeeTeam,
    plannedShift?.start,
    plannedShift?.end,
    plannedShift?.label,
    teamTimezone,
  ]);

  useEffect(() => {
    if (!manualShift && !successVisibility) {
      const clockInDate = getAutoSuggest(liveTime);

      const { given_name, family_name } = auth?.user || {};

      let note = "";
      if (given_name === "Ermis" && family_name === "Dauti") {
        note =
          "<p><span style='background-color: rgb(255, 251, 230);'>vonesë</span></p>";
      }
      setNewShift((prev) => ({ ...prev, clockInDate, note }));
    }
  }, [liveTime, manualShift, auth, plannedShift?.start]);

  // Anti-stale clock-in: while the modal is open and the user hasn't
  // manually edited the time, keep the suggested clock-in second-fresh.
  // This ensures that if the modal sits open for a few minutes and the
  // user finally clicks "Fillo Turnin", the recorded time is "now",
  // not whatever was loaded when the modal first rendered.
  const [nowTick, setNowTick] = useState(() => dayjsTZ());
  useEffect(() => {
    if (!newShiftVisib || manualShift || successVisibility) return;
    const id = setInterval(() => {
      const now = dayjsTZ();
      setNowTick(now);
      setNewShift((prev) => ({ ...prev, clockInDate: getAutoSuggest(now) }));
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newShiftVisib, manualShift, successVisibility, plannedShift?.start]);

  //region DERIVED UI VALUES
  const userFullName = `${auth?.user?.given_name || ""} ${
    auth?.user?.family_name || ""
  }`.trim();

  // Delta between scheduled start and actual clock-in (positive = late).
  const startDeltaMinutes = useMemo(() => {
    if (!plannedShift?.start || !newShift?.clockInDate) return null;
    return dayjsTZ(newShift.clockInDate).diff(plannedShift.start, "minute");
  }, [plannedShift, newShift.clockInDate]);

  const noteCharCount = useMemo(
    () => countNoteChars(newShift?.note),
    [newShift?.note]
  );

  const noteRequired = graceWarnings.enable;

  const shiftWindowSuspicious =
    !!plannedShift && plannedShift.minutes <= 0;

  // Disabled-hours array for the clock-face picker: disallow all hours
  // strictly after the current hour (cannot start a shift in the
  // future). This mirrors the AntD picker's `getDisabledAfterCustomHours`.
  const clockInDisabledHours = useMemo(() => {
    const nowH = dayjsTZ(nowTick).hour();
    const arr = [];
    for (let h = nowH + 1; h <= 23; h += 1) arr.push(h);
    return arr;
  }, [nowTick]);

  //region RETURN
  return (
    <>
      <Modal
        destroyOnHidden={true}
        width={760}
        className={`shiftc-modal shiftc-modal--fillo ${
          isDarkMode ? "dark" : ""
        }`}
        wrapClassName="shiftc-modal-wrap--noscroll"
        title={
          <div
            className="shiftc-headband__row"
            style={{ width: "100%", justifyContent: "space-between" }}
          >
            <div className="shiftc-headband__row">
              <span>Fillo Turnin</span>
              <Tag color="processing">Konfirmim fillimi</Tag>
            </div>
            <div className="shiftc-mode-toggle">
              <span className="shiftc-mode-toggle__label">Modo:</span>
              <Segmented
                size="small"
                value={pickerMode}
                onChange={(v) => setPickerMode(v)}
                options={[
                  { label: "Klasik", value: "classic" },
                  { label: "Orë", value: "clock" },
                ]}
              />
            </div>
          </div>
        }
        open={newShiftVisib}
        onCancel={() => setNewShiftVisib(false)}
        centered={true}
        maskClosable={false}
        footer={
          <>
            <div className="shiftc-footer-left">
              <Button onClick={() => setNewShiftVisib(false)}>
                Mbyll faqen
              </Button>
            </div>
            <div className="shiftc-footer-right">
              <Button
                type="primary"
                loading={saving}
                disabled={saving}
                icon={<PlayCircleFilled />}
                onClick={onSave}
                style={{ background: "#1da193", borderColor: "#1da193" }}
              >
                Fillo Turnin
              </Button>
            </div>
          </>
        }
      >
        <div className="shiftc-headband">
          <div className="shiftc-headband__row">
            <span className="shiftc-headband__name">{userFullName}</span>
            {dayType ? <Tag color="default">{dayType}</Tag> : null}
            <Tag color="default">{dayjsTZ().format("DD/MM/YYYY")}</Tag>
          </div>
          <div className="shiftc-headband__subtitle">
            Ju jeni duke filluar një turn — siguroni të dhënat para se ta
            konfirmoni.
            {plannedShift ? (
              <>
                {" "}
                Turni i përcaktuar: <strong>{plannedShift.label}</strong>.
              </>
            ) : null}
          </div>
        </div>

        {shiftWindowSuspicious ? (
          <Alert
            className="shiftc-alert"
            type="warning"
            showIcon
            message="Intervali i turnit nuk është konfiguruar saktë."
            description={`Turni i përcaktuar është ${plannedShift?.label}. Kontaktoni administratorin nëse vazhdon.`}
          />
        ) : null}

        {plannedShift?.start ? (
          <Alert
            className="shiftc-alert"
            type="info"
            showIcon
            message={plannedWindowHint(plannedShift, teamTimezone)}
          />
        ) : null}

        {gracePeriod > 0 || graceBypass ? (
          <Alert
            className="shiftc-alert"
            type="info"
            showIcon
            message={graceInfoMessage("in", gracePeriod, graceBypass)}
          />
        ) : null}

        {graceWarnings.enable ? (
          <Alert
            className="shiftc-alert"
            type="warning"
            showIcon
            message={graceWarnings.text}
          />
        ) : null}

        <div className="shiftc-elapsed">
          <div>
            <div className="shiftc-elapsed__label">Planifikim i turnit</div>
            <div className="shiftc-elapsed__value">
              {plannedShift?.label || "—"}
            </div>
            <div className="shiftc-elapsed__hint">
              {plannedShift
                ? `Kohëzgjatja e planifikuar: ${formatHm(
                    plannedShift.minutes
                  )}`
                : "Nuk ka një orar të caktuar për sot."}
            </div>
          </div>
          <span className="shiftc-elapsed__pill">
            <ClockCircleOutlined />
            Tani: {dayjsTZ(nowTick).format("HH:mm:ss")}
          </span>
        </div>

        <div className="shiftc-cards">
          <div className="shiftc-card shiftc-card--active">
            <div className="shiftc-card__label">
              <span className="shiftc-required-asterisk">*</span>
              <LoginOutlined /> Fillimi i turnit
            </div>
            <div className="shiftc-card__pickerRow">
              {pickerMode === "clock" ? (
                <ClockFaceTimePicker
                  value={newShift.clockInDate}
                  onChange={onEditClockIn}
                  isDarkMode={isDarkMode}
                  disabledHours={clockInDisabledHours}
                  placeholder="HH:MM"
                />
              ) : (
                <TimePicker
                  className="shiftc-timepicker"
                  value={newShift.clockInDate}
                  placeholder="HH:MM"
                  classNames={{
                    popup: {
                      root: `shiftc-timepicker-dropdown ${
                        isDarkMode ? "darkDropdown" : ""
                      }`,
                    },
                  }}
                  format="HH:mm"
                  onChange={onEditClockIn}
                  disabledTime={() => ({
                    disabledHours: getDisabledAfterCustomHours,
                    disabledMinutes: (hour) => getDisabledAfterMinutes(hour),
                  })}
                  showNow={false}
                  allowClear={false}
                  ref={clockinRef}
                />
              )}
            </div>
            <div className="shiftc-presets">
              <Button
                className="shiftc-preset-chip shiftc-preset-chip--primary"
                icon={<ClockCircleOutlined />}
                onClick={() => {
                  setManualShift(false);
                  const now = dayjsTZ();
                  setNowTick(now);
                  setNewShift((prev) => ({
                    ...prev,
                    clockInDate: getAutoSuggest(now),
                  }));
                }}
              >
                Tani
                <span className="shiftc-preset-chip__time">
                  {dayjsTZ(nowTick).format("HH:mm")}
                </span>
              </Button>
              {gracePeriod > 0 ? (
                <>
                  <Button
                    className="shiftc-preset-chip"
                    onClick={() =>
                      onEditClockIn(dayjsTZ().subtract(5, "minute"))
                    }
                  >
                    −5 min
                    <span className="shiftc-preset-chip__time">
                      {dayjsTZ().subtract(5, "minute").format("HH:mm")}
                    </span>
                  </Button>
                  {gracePeriod >= 15 ? (
                    <Button
                      className="shiftc-preset-chip"
                      onClick={() =>
                        onEditClockIn(dayjsTZ().subtract(15, "minute"))
                      }
                    >
                      −15 min
                      <span className="shiftc-preset-chip__time">
                        {dayjsTZ().subtract(15, "minute").format("HH:mm")}
                      </span>
                    </Button>
                  ) : null}
                </>
              ) : null}
            </div>
            <div className="shiftc-card__sub">
              {manualShift
                ? startDeltaMinutes !== null && startDeltaMinutes > 1
                  ? `Vonesë ${startDeltaMinutes} min ndaj planit`
                  : "Orari u modifikua manualisht"
                : "Mund të shkruani direkt HH:MM ose përdorni ‘Tani’"}
            </div>
          </div>

          <div className="shiftc-card shiftc-card--muted">
            <div className="shiftc-card__label">
              <LogoutOutlined /> Mbarimi
            </div>
            <div className="shiftc-card__pickerRow">
              <TimePicker
                className="shiftc-timepicker"
                value={newShift.clockOutDate}
                placeholder="—"
                disabled={true}
                format="HH:mm"
                classNames={{
                  popup: {
                    root: `shiftc-timepicker-dropdown ${
                      isDarkMode ? "darkDropdown" : ""
                    }`,
                  },
                }}
                disabledTime={() => ({
                  disabledHours:
                    newShift.clockInDate !== null
                      ? () => getDisabledBeforeCustomHours(newShift.clockInDate)
                      : () => [],
                  disabledMinutes:
                    newShift.clockInDate !== null
                      ? (hour) =>
                          getDisabledBeforeCustomMinutes(
                            newShift.clockInDate,
                            hour
                          )
                      : () => [],
                })}
                onChange={onEditClockOut}
                showNow={false}
                allowClear={false}
              />
            </div>
            <div className="shiftc-card__sub">
              Përcaktohet kur përfundon turni.
            </div>
          </div>

          <div className="shiftc-card shiftc-card--muted">
            <div className="shiftc-card__label">
              <CalendarOutlined /> Data e turnit
            </div>
            <div className="shiftc-card__value">
              {dayjsTZ(dateDayJs).format("DD/MM/YYYY")}
            </div>
            <div className="shiftc-card__sub">
              Llogaritet automatikisht
            </div>
          </div>

        </div>

        <div
          className={`shiftc-notes ${
            noteRequired && noteCharCount < MIN_NOTE_CHARS
              ? "shiftc-required-glow"
              : ""
          }`}
        >
          <div className="shiftc-notes__head">
            <span className="shiftc-notes__title">
              {noteRequired ? (
                <span className="shiftc-required-asterisk">*</span>
              ) : null}
              Shënimi i fillimit
            </span>
            {noteRequired ? (
              <span className="shiftc-notes__required">
                I detyrueshëm (min. {MIN_NOTE_CHARS} karaktere) ·{" "}
                {noteCharCount}/{MIN_NOTE_CHARS}
              </span>
            ) : (
              <span className="shiftc-card__sub">
                Opsional · {noteCharCount} karaktere
              </span>
            )}
          </div>
          <RichTextEditor
            className={"to-note-textarea"}
            value={newShift.note}
            onChange={(e) => setNewShift((prev) => ({ ...prev, note: e }))}
          />
        </div>

        <div className="shiftc-summary">
          <span>
            Statusi: <strong>Për konfirmim</strong>
          </span>
          {startDeltaMinutes !== null ? (
            <span>
              Diferenca nga plani:{" "}
              <strong
                className={
                  startDeltaMinutes > 0
                    ? "shiftc-summary__delta-pos"
                    : "shiftc-summary__delta-neg"
                }
              >
                {startDeltaMinutes > 0 ? "+" : ""}
                {startDeltaMinutes} min
              </strong>
            </span>
          ) : null}
          {/* Original "Lloji i turnit" Select — preserved as-is from
              the pre-redesign modal: disabled, programFields-driven,
              dark-mode dropdown class. Do not change the population
              logic. Rendered inline & compact here so it doesn't eat
              a full card row. */}
          <span className="shiftc-summary__type">
            <span className="shiftc-required-asterisk">*</span>
            Lloji:&nbsp;
            <Select
              className="daytype-select shiftc-shifttype-select shiftc-shifttype-select--inline"
              classNames={{ popup: { root: isDarkMode ? "darkDropdown" : undefined } }}
              disabled
              showSearch
              size="small"
              value={dayType}
              placeholder="—"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {programFields
                ?.find((el) => el.fieldId === CLOCKIN_TYPES_FIELD_ID)
                ?.fieldOptions?.map((el, idx) => (
                  <Option key={`shiftType-${idx}`} value={el?.name}>
                    {el?.description}
                  </Option>
                ))}
            </Select>
          </span>
        </div>

        <CrashShift
          type={"new"}
          crashData={crashData}
          open={crashError}
          setCrashError={setCrashError}
        />
      </Modal>
      {!!successVisibility ? (
        <SuccessComponent
          open={successVisibility}
          setOpen={setSuccessVisibility}
          text={`Turni u fillua me sukses në orën ${dayjsTZ(
            newShift.clockInDate
          ).format("HH:mm")} `}
          onOk={handleReset}
        />
      ) : null}
    </>
  );
}

export default NewUserShift;
